import { log } from "../../../";

import * as React from "react";
import Dropzone from "react-dropzone";
import { observable, action, runInAction } from "mobx";
import { observer } from "mobx-react";

import Typography from "@material-ui/core/Typography";

import { CreateListsStore } from "../../../stores/CreateListsStore";
import CircularProgress from "@material-ui/core/CircularProgress";

import { strings } from "../../../core/localizedStrings";

import * as loadImage from "blueimp-load-image";
var dataURLtoBlob = require("blueimp-canvas-to-blob");

var Row = require("react-flexbox-grid").Row;
var Col = require("react-flexbox-grid").Col;

export interface UploadImageDropZoneProps extends React.Props<any> {
    store: CreateListsStore;
}

const MAX_FILE_SIZE = 2000 * 1000; // 2 Mb
const MAX_FILES = 20;

@observer
export class UploadImageDropZone extends React.Component<UploadImageDropZoneProps> {
    @observable
    private isUploading: boolean = false;

    public render() {
        let style = {
            borderStyle: "dashed",
            borderRadius: 5,
            cursor: "pointer",
            margin: "0 auto",
        };

        return (
            <Dropzone
                accept="image/*"
                disableClick={this.isUploading}
                maxSize={MAX_FILE_SIZE}
                style={style}
                onDrop={this.onDropImage}
            >
                <Row center="xs">
                    <Col>
                        <Typography variant="subtitle1" style={{ padding: 15 }}>
                            {strings.uploadImagesDropzoneInstructions}
                        </Typography>
                        <Row center="xs">{this.renderProgress()}</Row>
                    </Col>
                </Row>
            </Dropzone>
        );
    }

    @action
    public componentWillReceiveProps(newProps: UploadImageDropZoneProps) {
        this.isUploading = false;
    }

    @action
    private onDropImage = async (acceptedFiles: Array<File>, rejectedFiles: Array<File>) => {
        if (this.isUploading) {
            return;
        }

        if (acceptedFiles.length === 0) {
            let errorMessage = strings.formatString(strings.noAcceptableFilesFound, MAX_FILE_SIZE / 1000 / 1000);
            this.props.store.rootStore.showInfoMessage({ text: errorMessage as string });
            return;
        }

        if (acceptedFiles.length > MAX_FILES) {
            let errorMessage = strings.formatString(strings.maxFilesUploadAttempt, MAX_FILES);
            this.props.store.rootStore.showInfoMessage({ text: errorMessage as string });
            return;
        }

        this.isUploading = true;
        await this.props.store.createAndSetRootFolderIfNecessary();

        this.props.store.totalUploads = acceptedFiles.length;
        this.props.store.uploadErrorCount = 0;
        this.props.store.uploadSuccessCount = 0;

        for (var file of acceptedFiles) {
            await this.uploadFile(file);
        }

        if (rejectedFiles !== undefined && rejectedFiles.length > 0) {
            log.verbose(`${rejectedFiles.length} files were rejected`);
            log.verbose(rejectedFiles);

            let errorMessage = rejectedFiles.length === 1 ? strings.rejectedFileFormat : strings.rejectedFilesFormat;
            errorMessage = strings.formatString(errorMessage, rejectedFiles.length) as string;

            this.props.store.rootStore.showInfoMessage({ text: errorMessage });
        }
    };

    private uploadFile = (file: File) => {
        return new Promise<any>((resolve) => {
            const loadImageOptions: any = { canvas: true };
            loadImage.parseMetaData(file, async (data) => {
                if (data.exif && data.exif.get("Orientation")) {
                    let orientation = data.exif.get("Orientation");
                    if (orientation !== 1) {
                        loadImageOptions.orientation = orientation;
                        console.log("Change orientation.");
                        loadImage(
                            file,
                            async (canvas) => {
                                let blob = dataURLtoBlob(canvas.toDataURL(file.type));
                                await this.props.store.uploadImage(blob);
                                resolve();
                            },
                            loadImageOptions
                        );
                    } else {
                        await this.props.store.uploadImage(file);
                        resolve();
                    }
                } else {
                    await this.props.store.uploadImage(file);
                    resolve();
                }
            });
        });
    };

    private renderProgress() {
        if (!this.isUploading) {
            return null;
        } else {
            return (
                <div>
                    <div>{strings.formatString(strings.uploadMessage, this.props.store.totalUploads)}</div>
                    <CircularProgress />
                </div>
            );
        }
    }
}
